import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { AuthService } from 'lib/auth/auth.service.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { HttpServiceError } from 'src/app/http/http.service.js';
import { match, P } from 'ts-pattern';
import { angularErrorHelper } from '../helpers/angular-error-helper.class.js';
import { AppLogService } from '../services/app-log.service.js';
import { NavigateService } from '../services/navigate.service.js';
import { AppError, AppErrorBehavior } from './error.models.js';

/*
Do not provide handler as singleton
*/
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	private get navigateService(): NavigateService {
		return this.injector.get<NavigateService>(NavigateService);
	}

	private get logService(): AppLogService {
		return this.injector.get<AppLogService>(AppLogService);
	}

	private get authService(): AuthService {
		return this.injector.get<AuthService>(AuthService);
	}

	constructor(
		// Use injector in error handler as per https://github.com/scttcper/ngx-toastr/issues/179
		private injector: Injector
	) {}

	async handleError(error: any): Promise<void> {
		console.error('Application encountered an error: ', error);

		try {
			const authUser = this.authService.authUser();
			const errorData = angularErrorHelper.extractErrorData(error);

			this.logService.latestError.set(errorData);

			const errorBehavior = this.getErrorBehavior(error, authUser);

			await this.logService.logErrorAsync({
				message: `${errorData.message}`,
				behavior: errorBehavior,
				stacktrace: errorData.stacktrace,
				email: this.authService.authUser()?.email
			});

			this.handleErrorNavigation(errorBehavior);

			// if the 'throw error' is used, the application will become unresponsive and the handle error would be called twice.
			// just log the error to console.
			// throw error;
		} catch (error) {
			this.handleErrorNavigation('default');
		}
	}

	private getErrorBehavior(error: any, authUser: UserDb.IAuthUser | undefined): AppErrorBehavior {
		return match(error)
			.returnType<AppErrorBehavior>()
			.with(P.instanceOf(HttpErrorResponse), (m) => {
				if (m.status === 401) {
					if (authUser) {
						return 'redirectToInsufficientPermissionPage';
					}
					return 'redirectToLoginPage';
				}

				if (m.status === 404) {
					return 'redirectTo404';
				}

				return 'default';
			})
			.with(P.instanceOf(HttpServiceError), (m) => {
				if (m.behavior) {
					return m.behavior;
				}

				return 'default';
			})
			.with(P.instanceOf(AppError), (m) => {
				return m.behavior;
			})
			.otherwise(() => {
				return 'default';
			});
	}

	private handleErrorNavigation(behavior: AppErrorBehavior): void {
		return match(behavior)
			.returnType<void>()
			.with('redirectTo404', () => {
				this.navigateService.notFoundPage().navigate();
			})
			.with('redirectToLoginPage', () => {
				this.navigateService.loginPage().navigate();
			})
			.with('redirectToInsufficientPermissionPage', () => {
				this.navigateService.insufficientPermissionsPage().navigate();
			})
			.with('redirectDeletedUser', () => {
				// logout user when deleted
				this.authService.logout();
				this.navigateService.loginPage().navigate();
			})
			.otherwise(() => {
				this.navigateService.errorPage().navigate();
			});
	}
}
